// Cellery Soapbox — annotation sidebar (CONTRACTS §6/§7).
// Lists annotation threads for the current version, each showing its comments
// oldest-first with a reply composer and a resolve toggle. A pending selection
// shows a composer that creates the annotation + its first comment.

import { useEffect, useState } from "react";
import { useAuth } from "../auth";
import { Icon } from "../components/Icon";
import { addComment, formatTimestamp, listComments, setResolved } from "../firestore";
import type { Anchor, Annotation, Comment } from "../types";

interface SidebarProps {
  docId: string;
  versionId: string;
  annotations: Annotation[];
  selectedAnnId: string | null;
  onSelect: (annId: string) => void;
  pending: Anchor | null;
  onCreatePending: (body: string) => Promise<void>;
  onCancelPending: () => void;
  onClose: () => void;
}

export function Sidebar({
  docId,
  versionId,
  annotations,
  selectedAnnId,
  onSelect,
  pending,
  onCreatePending,
  onCancelPending,
  onClose,
}: SidebarProps) {
  return (
    // Escape is bound HERE rather than on document: Modal, SharePanel, Menu and
    // presentation mode all arm document-level Escape handlers with no focus
    // gate, so a document listener would dismiss two things at once.
    <aside
      id="soapbox-comments"
      className="sidebar"
      aria-label="Comments"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="sidebar-head">
        <div className="sidebar-head-text">
          <h2>Comments</h2>
          <p className="caption">
            {annotations.length} {annotations.length === 1 ? "thread" : "threads"}
          </p>
        </div>
        {/* The panel has to carry its own way out. The toolbar toggle can close
            it too, but it is across the screen, looks like its non-toggle
            neighbours, and readers reasonably never found it. */}
        <button
          type="button"
          className="btn btn-ghost tap icon-btn tip-end"
          data-tip="Hide comments"
          aria-label="Hide comments"
          aria-controls="soapbox-comments"
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
      </div>
      <div className="sidebar-scroll">
        {pending && (
          <PendingComposer
            anchor={pending}
            onSubmit={onCreatePending}
            onCancel={onCancelPending}
          />
        )}

        {annotations.length === 0 && !pending && (
          <p className="empty-state">
            No comments yet. Select text in the document to start a thread.
          </p>
        )}

        {annotations.map((ann) => (
          <Thread
            key={ann.annId}
            docId={docId}
            versionId={versionId}
            annotation={ann}
            selected={ann.annId === selectedAnnId}
            onSelect={() => onSelect(ann.annId)}
          />
        ))}
      </div>
    </aside>
  );
}

function PendingComposer({
  anchor,
  onSubmit,
  onCancel,
}: {
  anchor: Anchor;
  onSubmit: (body: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      await onSubmit(text);
      setBody("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="thread is-selected">
      <p className="thread-quote">“{anchor.exact}”</p>
      <div className="composer">
        <label htmlFor="soapbox-new-comment" className="visually-hidden">
          New comment
        </label>
        <textarea
          id="soapbox-new-comment"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add your comment"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <div className="composer-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={busy || body.trim().length === 0}
          >
            Comment
          </button>
        </div>
      </div>
    </div>
  );
}

function Thread({
  docId,
  versionId,
  annotation,
  selected,
  onSelect,
}: {
  docId: string;
  versionId: string;
  annotation: Annotation;
  selected: boolean;
  onSelect: () => void;
}) {
  const { user, emailLower } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsub = listComments(docId, versionId, annotation.annId, setComments);
    return unsub;
  }, [docId, versionId, annotation.annId]);

  const submitReply = async () => {
    const text = reply.trim();
    if (!text || !user) return;
    setBusy(true);
    try {
      await addComment(
        docId,
        versionId,
        annotation.annId,
        text,
        user.uid,
        emailLower
      );
      setReply("");
    } finally {
      setBusy(false);
    }
  };

  const toggleResolved = () =>
    void setResolved(docId, versionId, annotation.annId, !annotation.resolved);

  const className =
    "thread" +
    (selected ? " is-selected" : "") +
    (annotation.resolved ? " is-resolved" : "");

  return (
    <div className={className}>
      <button
        type="button"
        className="thread-quote"
        onClick={onSelect}
        aria-pressed={selected}
      >
        “{annotation.anchor.exact}”
      </button>

      <div className="thread-status">
        <span
          className={
            "status-dot " +
            (annotation.resolved ? "status-dot--resolved" : "status-dot--open")
          }
          aria-hidden="true"
        />
        <span>{annotation.resolved ? "Resolved" : "Open"}</span>
      </div>

      {comments.map((c) => (
        <div className="comment" key={c.commentId}>
          <div className="comment-author">
            {c.authorEmail} · <time>{formatTimestamp(c.createdAt)}</time>
          </div>
          <div className="comment-body">{c.body}</div>
        </div>
      ))}

      <div className="composer">
        <label
          htmlFor={`soapbox-reply-${annotation.annId}`}
          className="visually-hidden"
        >
          Reply to thread
        </label>
        <textarea
          id={`soapbox-reply-${annotation.annId}`}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Reply"
        />
        <div className="composer-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={toggleResolved}
            aria-pressed={annotation.resolved}
          >
            {annotation.resolved ? "Reopen" : "Resolve"}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submitReply()}
            disabled={busy || reply.trim().length === 0}
          >
            Reply
          </button>
        </div>
      </div>
    </div>
  );
}
